import { ToggleButton } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { PracticeCardSkeleton, type PracticeStatus } from '@/entities/practice';
import { useInDesktopShell } from '@/shared/lib/desktopLayout';
import { cn } from '@/shared/lib/cn';

/**
 * Куски `TopicsList`, которые нужны и самому списку, и скелету страницы
 * (`TopicsListSkeleton`, стартовый чанк): отдельным лёгким файлом, чтобы
 * скелет рисовал ровно ту же разметку и не тянул за собой список.
 */

export type StatusTab = 'all' | PracticeStatus;

const STATUS_TABS: { key: StatusTab; labelKey: string }[] = [
  { key: 'all', labelKey: 'tabAll' },
  { key: 'new', labelKey: 'tabNew' },
  { key: 'seen', labelKey: 'tabNotCompleted' },
  { key: 'completed', labelKey: 'tabCompleted' },
];

/** Чёрные вместо стандартных зелёных (`--accent-soft`) — на этом экране статус-фильтр не должен спорить с зелёными бейджами статуса на самих карточках. */
const STATUS_TAB_SELECTED_CLASS =
  '[--toggle-button-bg-selected:var(--color-ink)] [--toggle-button-bg-selected-hover:var(--color-ink)] [--toggle-button-fg-selected:var(--color-glass)]';

/** Сколько скелетов-карточек показать до первой загрузки — три ряда по два, чтобы точно заполнить экран без домысливания реальной длины списка. */
const SKELETON_COUNT = 6;

export function StatusTabs({ value, onChange }: { value: StatusTab; onChange: (tab: StatusTab) => void }) {
  const { t } = useTranslation('TopicsList');
  return (
    <div className="scroll-area -mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
      {STATUS_TABS.map(({ key, labelKey }) => (
        <ToggleButton
          key={key}
          size="sm"
          isSelected={value === key}
          onChange={() => onChange(key)}
          className={STATUS_TAB_SELECTED_CLASS}
        >
          {t(labelKey)}
        </ToggleButton>
      ))}
    </div>
  );
}

export function TopicsGridSkeleton() {
  // На компьютере колонка 640 px: в две колонки квадратные карточки выходили
  // по 290 px, и на экран влезал один ряд. Три — шесть карточек превью в два ряда.
  const inDesktopShell = useInDesktopShell();
  return (
    <div className={cn('grid gap-4', inDesktopShell ? 'grid-cols-3' : 'grid-cols-2')}>
      {Array.from({ length: SKELETON_COUNT }, (_, index) => (
        <PracticeCardSkeleton key={index} />
      ))}
    </div>
  );
}

/**
 * Список до прихода кода страницы: вкладки на «Все», как при первом
 * открытии, и та же сетка скелетов, что `TopicsList` показывает до данных.
 */
export function TopicsListSkeleton() {
  return (
    <>
      <StatusTabs value="all" onChange={noop} />
      <TopicsGridSkeleton />
    </>
  );
}

function noop() {
  // Код страницы ещё не пришёл — фильтровать нечего.
}
